import { createInstance, dateReviver } from '@/api';
import {
    DeliveryProcessDefinitionDependenciesReportDto,
    CreateDeliveryProcessDefinitionDto,
    DeliveryProcessDefinitionCascadeDeleteOptionsDto,
    DeliveryProcessDefinitionClient,
    DeliveryProcessDefinitionDetailDto,
    DeliveryProcessDefinitionExportOptionsDto,
    UpdateDeliveryProcessDefinitionDto,
    DeliveryProcessDefinitionReportingOptionsDto,
    ImportSpecificationDto,
    ImportSpecificationType,
    GeckoTestDto
} from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { useDeliverySpecificationStore } from '../delivery-specification/delivery-specification.store';
import { useDeliveryProcessDefinitionEditStore } from './delivery-process-definition-edit.store';
import { useOperationSpecificationStore } from '../operation-specification/operation-specification.store';
import { ENUM_StatusTypes } from '../../common';
import downloadBlob from '@/helpers/download.helpers';

export interface DeliveryProcessDefinitionDetailStoreState {
    detail?: DeliveryProcessDefinitionDetailDto;
    dependenciesReport?: DeliveryProcessDefinitionDependenciesReportDto;
    loading: boolean;
    reloading: boolean;
}

export const client = new DeliveryProcessDefinitionClient('', createInstance(), dateReviver);

export const useDeliveryProcessDefinitionDetailStore = defineStore(
    'configuration-delivery-process-definition-detail-store',
    {
        state: (): DeliveryProcessDefinitionDetailStoreState => ({
            detail: null,
            loading: false,
            reloading: false
        }),
        getters: {
            isDraft(): boolean {
                return this.detail?.statusId === ENUM_StatusTypes.Draft.id;
            }
        },
        actions: {
            async getDetail(id: number) {
                return await client.getDetail(id);
            },
            async get(id: number, reloading: boolean = false) {
                reloading ? (this.reloading = true) : (this.loading = true);
                try {
                    this.detail = await client.getDetail(id);
                    return this.detail;
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('deliveryprocessdefinition.detail.error.get.title'),
                        error,
                        false
                    );
                } finally {
                    reloading ? (this.reloading = false) : (this.loading = false);
                }
            },
            async getDependenciesReport(id: number, reloading: boolean = false) {
                reloading ? (this.reloading = true) : (this.loading = true);
                try {
                    this.dependenciesReport = await client.getDependenciesReport(id);
                    return this.dependenciesReport;
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('deliveryprocessdefinition.detail.error.get.title'),
                        error,
                        false
                    );
                } finally {
                    reloading ? (this.reloading = false) : (this.loading = false);
                }
            },
            async create(dto: CreateDeliveryProcessDefinitionDto): Promise<number> {
                this.loading = true;
                let response = null;
                try {
                    response = await client.create(dto);
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('deliveryprocessdefinition.create.error.title'),
                        error,
                        false
                    );
                } finally {
                    useDeliveryProcessDefinitionEditStore().closeEdit();
                    this.loading = true;
                }
                return response?.id;
            },
            async update(id: number, dto: UpdateDeliveryProcessDefinitionDto) {
                this.reloading = true;
                try {
                    await client.update(id, dto);
                    useAlertStore().setSuccess(i18n.global.t('deliveryprocessdefinition.detail.success.update.title'));
                    await this.get(id, true);
                    return true;
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('deliveryprocessdefinition.detail.error.update.title'),
                        error,
                        false
                    );
                } finally {
                    useDeliveryProcessDefinitionEditStore().closeEdit();
                    this.reloading = false;
                }
            },
            async delete(id: number) {
                this.reloading = true;
                try {
                    await client.delete(id);
                    useAlertStore().setSuccess(i18n.global.t('deliveryprocessdefinition.detail.success.delete.title'));
                    return true;
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('deliveryprocessdefinition.detail.error.delete.title'),
                        error,
                        false
                    );
                } finally {
                    this.reloading = false;
                }
            },
            async changeStatus(id: number, statusType: string): Promise<boolean> {
                this.reloading = true;
                try {
                    await client.changeStatus(id, statusType);
                    useAlertStore().setSuccess(i18n.global.t('deliveryprocessdefinition.detail.success.update.title'));
                    await this.get(id, true);
                    return true;
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('deliveryprocessdefinition.detail.error.update.title'),
                        error
                    );
                    return false;
                } finally {
                    this.reloading = false;
                }
            },
            async duplicate(
                id: number,
                {
                    duplicateProductProcessDefinitions = false,
                    duplicateDeliveryQualityChecks = false
                }: { duplicateProductProcessDefinitions: boolean; duplicateDeliveryQualityChecks: boolean }
            ): Promise<number> {
                let response = null;
                try {
                    response = await client.duplicate(id, {
                        duplicateEntityModels: true,
                        duplicateProductProcessDefinitions,
                        duplicateDeliveryQualityChecks
                    });
                    useAlertStore().setSuccess(
                        i18n.global.t('deliveryprocessdefinition.detail.success.duplicate.title')
                    );
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('deliveryprocessdefinition.detail.error.duplicate.title'),
                        error
                    );
                }
                return response?.id;
            },
            async export(id: number, options: DeliveryProcessDefinitionExportOptionsDto): Promise<void> {
                try {
                    const file = await client.export(id, options);

                    return new Promise((resolve) => {
                        downloadBlob(file.data, file.fileName, () => {
                            resolve();
                        });
                    });
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('deliveryprocessdefinition.detail.error.export.title'),
                        error
                    );
                }
            },
            async import(file: File, parentDefinitionId: number = null): Promise<number> {
                let response = null;
                try {
                    response = await client.import(parentDefinitionId, { data: file, fileName: file.name });
                    useAlertStore().setSuccess(i18n.global.t('deliveryprocessdefinition.detail.success.import.title'));
                } catch (error: any) {
                    useAlertStore().handleException(
                        i18n.global.t('deliveryprocessdefinition.detail.error.import.title'),
                        error
                    );
                }
                return response?.id;
            },
            async cascadeDelete(
                id: number,
                options: DeliveryProcessDefinitionCascadeDeleteOptionsDto = { deleteEntityModels: true }
            ) {
                this.reloading = true;
                try {
                    await client.cascadeDelete(id, options);
                    useAlertStore().setSuccess(i18n.global.t('deliveryprocessdefinition.detail.success.delete.title'));
                    return true;
                } catch (error: any) {
                    useAlertStore().handleException(
                        i18n.global.t('deliveryprocessdefinition.detail.error.delete.title'),
                        error
                    );
                } finally {
                    useDeliveryProcessDefinitionEditStore().closeEdit();
                    this.reloading = false;
                }
            },
            async validateCascadeDelete(id: number): Promise<boolean> {
                try {
                    const isValid = await client.cascadeDeleteValidate(id);
                    return isValid;
                } catch (error) {
                    console.error('validateCascadeDelete', error);
                    return false;
                }
            },
            async generateReport(id: number, options: DeliveryProcessDefinitionReportingOptionsDto): Promise<void> {
                try {
                    const file = await client.generateReport(id, options);

                    return new Promise((resolve) => {
                        downloadBlob(file.data, file.fileName, () => {
                            resolve();
                        });
                    });
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('deliveryprocessdefinition.detail.error.generateReport.title'),
                        error
                    );
                }
            },
            async importSpecification(id: number, importSpecification: ImportSpecificationDto) {
                this.reloading = true;

                try {
                    await client.importSpecification(id, importSpecification);

                    useAlertStore().setSuccess(
                        i18n.global.t('deliveryprocessdefinition.detail.success.importSpecification.title')
                    );

                    if (importSpecification.target == ImportSpecificationType.DeliverySpecification) {
                        await useDeliverySpecificationStore().getByDefinition(id, true);
                    }
                    if (importSpecification.target == ImportSpecificationType.OperationSpecification) {
                        await useOperationSpecificationStore().getByDefinition(id, true);
                    }
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('deliveryprocessdefinition.detail.error.importSpecification.title'),
                        error
                    );
                } finally {
                    useDeliveryProcessDefinitionEditStore().closeEdit();
                    this.reloading = false;
                }
            },
            clear(id: number) {
                this.detail = null;
                this.dependenciesReport = null;
                useDeliverySpecificationStore().clear(id);
                useOperationSpecificationStore().clear(id);
            }
        }
    }
);
